import { Link } from "react-router";
import { ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { blogPosts } from "../blog/posts";
import { getSiteOrigin } from "../site";

function formatDate(date: string) {
  return new Date(date).toLocaleDateString("de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function getImageCreditLabel(url: string) {
  return url.includes("unsplash.com") ? " / Unsplash" : "";
}

export function Blog() {
  return (
    <div className="bg-stone-50 pt-20 text-gray-950">
      <Seo
        title="Blog"
        description="Praxiswissen zu Website-Kosten, lokaler SEO, Relaunch, Baukasten-Vergleich und Website-Strategie für kleine Unternehmen."
        path="/blog"
        keywords={[
          "blog website kleine unternehmen",
          "lokale seo",
          "website kosten",
          "website relaunch",
          "website erstellen lassen",
          "baukasten oder agentur",
          "website handwerksbetrieb",
          "website wartung",
          "dsgvo website",
          "website as a service",
          "waas",
        ]}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "mutzur Blog",
          description:
            "Praxiswissen zu Website-Kosten, lokaler SEO, Relaunch, Baukasten-Vergleich und Website-Strategie für kleine Unternehmen.",
          url: `${getSiteOrigin()}/blog`,
          publisher: {
            "@type": "Organization",
            name: "mutzur",
          },
        }}
      />

      <section className="border-b border-stone-200 bg-white">
        <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-gray-500">Blog</p>
          <h1 className="mt-4 max-w-3xl text-4xl leading-tight lg:text-5xl">
            Inhalte für Sichtbarkeit, Vertrauen und bessere Website-Entscheidungen
          </h1>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-gray-700 sm:text-lg">
            Der Blog richtet sich an kleine Unternehmen, die ihre Website wirtschaftlich,
            sichtbar und verlässlich betreiben wollen. Die Beiträge sind bewusst praxisnah und
            auf typische Entscheidungen im Alltag ausgerichtet, von lokaler SEO über Relaunch-
            Fragen bis zum Vergleich zwischen Baukasten und betreuter Website-Lösung.
          </p>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="mx-auto grid max-w-5xl gap-6 px-4 sm:px-6 lg:px-8">
          {blogPosts.map((post) => (
            <article
              key={post.slug}
              className="overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm transition-transform duration-300 hover:-translate-y-0.5"
            >
              <Link
                to={`/blog/${post.slug}`}
                className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2"
              >
                <img
                  src={post.imageUrl}
                  alt={post.imageAlt}
                  className="aspect-[16/8] w-full object-cover"
                  loading="lazy"
                />

                <div className="p-6 sm:p-8">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                      <span className="rounded-full bg-stone-100 px-3 py-1 font-medium text-gray-700">
                        {post.category}
                      </span>
                      <span>{formatDate(post.publishedAt)}</span>
                      <span>{post.readingTime}</span>
                    </div>
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-black transition-colors hover:text-gray-700">
                      Beitrag lesen
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>

                  <h2 className="mt-5 text-2xl leading-tight text-gray-950">{post.title}</h2>
                  <p className="mt-4 max-w-3xl text-base leading-relaxed text-gray-700">
                    {post.excerpt}
                  </p>
                </div>
              </Link>

              <p className="px-6 pb-6 text-xs text-gray-500 sm:px-8 sm:pb-8">
                Foto:{" "}
                <a
                  href={post.imageCreditUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="underline decoration-stone-300 underline-offset-4"
                >
                  {post.imageCreditName}
                  {getImageCreditLabel(post.imageCreditUrl)}
                </a>
              </p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
